import * as React from "react";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkFloatingFullWidthButton, UitkTertiaryButton, UitkButtonSize } from "uitk-react-button";
import { liveAnnounce, Level } from "uitk-react-utils";
import { UitkRadio, UitkRadioGroup } from "uitk-react-switch";
import { UitkLink, UitkLinkAlign, UitkLinkTypeSize } from "uitk-react-link";
import { UitkIcon } from "uitk-react-icons";
import { Room } from "./Room";
import { locKeys } from "../l10n";
import { TravelersPickerProps, AgeOption } from "../typings";
import { InfantSeating } from "../InfantSeating";
import { travelersMetadata } from "../utils";
import { useLocalization } from "@shared-ui/localization-context";
import { inject } from "mobx-react";
import { getTracker } from "utils/TrackingUtils";

export const TravelersPicker = inject("analytics")((props: TravelersPickerProps) => {
  const childAgeOptions: AgeOption[] = [];
  const infantAgeOptions: AgeOption[] = [];
  const { formatText } = useLocalization();

  const populateAgeOptions = (
    minAge: number,
    maxAge: number,
    youngestAgeTextToken: string,
    ageOptions: AgeOption[]
  ) => {
    for (let i = minAge; i <= maxAge; i++) {
      const name: string = i === minAge && !!youngestAgeTextToken ? formatText(youngestAgeTextToken) : i.toString();
      ageOptions.push({
        value: i.toString(),
        name,
      });
    }
  };

  populateAgeOptions(
    props.config.minChildAge,
    props.config.maxChildAge,
    props.config.youngestChildAgeTextToken,
    childAgeOptions
  );
  populateAgeOptions(
    props.config.minInfantAge,
    props.config.maxInfantAge,
    props.config.youngestInfantAgeTextToken,
    infantAgeOptions
  );

  const onAddRoom = () => {
    props.setTravelers({
      rooms: props.travelers.rooms.concat({ adults: 1, children: [], infants: [] }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.roomAdded, props.travelers.rooms.length + 1), Level.POLITE);
    props.setTotalRoomRefs(props.totalRoomRefs + 1);

    tracker.trackEvent(`${_guests}add-room`);
  };

  const onInfantSeatingChange = (event: React.ChangeEvent<HTMLFormElement>) => {
    props.setTravelers({
      rooms: props.travelers.rooms,
      infantSeating: event.currentTarget.value,
    });
  };

  const onSave = () => {
    props.onSave(props.travelers);
  };

  const tracker = getTracker(props.lobState.trackingEnabled, props.analytics, props.lobState.moduleName);
  const _guests = "guests.";

  const { numOfChildren, numOfInfants } = travelersMetadata(props.travelers);
  const showAddRoomButton: boolean = props.config.withRooms && props.travelers.rooms.length < props.config.maxRooms;
  const showInfantSeatingOptions: boolean = props.config.withInfants && props.config.withFlight && !!numOfInfants;
  const showAirlineAgeRules: boolean =
    props.config.withFlight &&
    props.config.withAges &&
    (!!numOfChildren || (props.config.withInfants && !!numOfInfants));
  const airlineChildSeatingMessage =
    !!numOfInfants && numOfInfants > 1 ? locKeys.airlineChildSeatingMessagePlural : locKeys.airlineChildSeatingMessage;

  return (
    <>
      <section className="uitk-cell all-x-padding-one all-t-padding-half all-b-padding-twelve uitk-scroll-vertical">
        {props.travelers.rooms.map((room, index) => (
          <Room
            setTravelers={props.setTravelers}
            travelers={props.travelers}
            key={index}
            index={index}
            room={room}
            config={props.config}
            roomRefs={props.roomRefs}
            totalRoomRefs={props.totalRoomRefs}
            setTotalRoomRefs={props.setTotalRoomRefs}
            childAgeOptions={childAgeOptions}
            infantAgeOptions={infantAgeOptions}
            lobState={props.lobState}
          />
        ))}
        {showAddRoomButton && (
          <div className="addRoomButton uitk-type-right">
            <UitkTertiaryButton onClick={onAddRoom} data-testid="add-room-button">
              {formatText(locKeys.addRoom)}
            </UitkTertiaryButton>
          </div>
        )}
        {showInfantSeatingOptions && (
          <div className="airlineChildSeating all-t-padding-six">
            <fieldset>
              <legend className="uitk-type-300 all-b-padding-two">{formatText(airlineChildSeatingMessage)}</legend>
              <UitkRadioGroup
                group="InfantSeating"
                onChange={onInfantSeatingChange}
                selected={props.travelers.infantSeating}
              >
                <UitkRadio id="ChildOnLap" labelClassName="all-r-padding-six" value={InfantSeating.ON_LAP}>
                  {formatText(locKeys.airlineSeatingOnLap)}
                </UitkRadio>
                <UitkRadio id="ChildInSeat" labelClassName="all-r-padding-six" value={InfantSeating.IN_SEAT}>
                  {formatText(locKeys.airlineSeatingInSeat)}
                </UitkRadio>
              </UitkRadioGroup>
            </fieldset>
          </div>
        )}
        {showAirlineAgeRules && (
          <div className="uitk-cell all-cell-1-1">
            <UitkLink iconPos={UitkLinkAlign.RIGHT} type={UitkLinkTypeSize.SMALL} className="airlineAgeRules">
              <a
                href="/p/info-main/h_fw_default?rhtx=HTX_FLTSRCH_RT#travs"
                id="airline-age-rules"
                target="_blank"
                rel="noopener"
              >
                {formatText(locKeys.airlineAgeRules)}
                <UitkIcon id="airline-age-rules-opens-in-new" name="open_in_new" title="Opens in a new window" />
              </a>
            </UitkLink>
          </div>
        )}
        <div className="all-t-padding-two all-b-padding-six" />
      </section>
      <UitkScrim className="guestsDoneBtn" type={UitkScrimType.BUTTON}>
        <UitkFloatingFullWidthButton
          onClick={onSave}
          size={UitkButtonSize.LARGE}
          isFullWidth
          data-testid="guests-done-button"
        >
          {formatText(locKeys.doneLabel)}
        </UitkFloatingFullWidthButton>
      </UitkScrim>
    </>
  );
});
